import { Dialog, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment } from 'react';
import { HiX } from 'react-icons/hi';

type ModalSize = 'small' | 'medium' | 'large' | 'x-large';

interface Props {
  className?: string;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: ModalSize;
}

function getSizeClass(size: ModalSize): string {
  switch (size) {
    case 'small':
      return 'sm:max-w-sm';
    case 'medium':
      return 'sm:max-w-md';
    case 'large':
      return 'sm:max-w-lg';
    case 'x-large':
      return 'w-full md:w-[50rem] m-8';
    default:
      throw new Error(`Invalid size: ${size as string}`);
  }
}

function Modal({
  className,
  isOpen,
  onClose,
  children,
  size = 'large',
}: Props): JSX.Element {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className={clsx('relative z-10', className)}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  'relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:p-6',
                  getSizeClass(size),
                )}
              >
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

interface ModalHeaderProps {
  className?: string;
  children: React.ReactNode;
  onClose: () => void;
}

Modal.Header = function ModalHeader({
  className,
  children,
  onClose,
}: ModalHeaderProps): JSX.Element {
  return (
    <div
      className={clsx(
        'flex w-full items-start justify-between space-x-4',
        className,
      )}
    >
      <h4>{children}</h4>
      <button type="button" onClick={onClose}>
        <HiX className="h-6 w-6 text-gray-500" />
      </button>
    </div>
  );
};

export default Modal;
